import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, CheckCircle2, Shield, TrendingUp, Users, Award, 
  Clock, Headphones, FileText, Calculator, Globe, Building2,
  Star, ChevronRight, Phone, Mail
} from 'lucide-react';
import PublicLayout from '@/components/layout/PublicLayout';

export default function HomePage() {
  const navigate = useNavigate();

  const services = [
    { 
      icon: FileText,
      title: 'Tax Filing', 
      description: 'Individual and business tax filing made simple', 
      features: ['1040 Filing', 'Business Returns', 'Tax Planning'],
      path: '/services' 
    },
    { 
      icon: Building2,
      title: 'LLC Formation', 
      description: 'Start your business with professional LLC setup', 
      features: ['State Filing', 'EIN Registration', 'Operating Agreement'],
      path: '/services' 
    },
    { 
      icon: Calculator,
      title: 'Tax Calculators', 
      description: 'Free tools to estimate your taxes and refunds', 
      features: ['Tax Estimator', 'Refund Calculator', 'Deduction Finder'],
      path: '/tools' 
    }
  ];

  const stats = [
    { value: '50', label: 'States Covered' },
    { value: '99%', label: 'Satisfaction Rate' }
  ];

  const whyChooseUs = [
    { 
      icon: Award,
      title: 'Expert Guidance', 
      description: 'Tax professionals ready to help you maximize your refund' 
    },
    { 
      icon: Clock,
      title: 'Fast & Easy', 
      description: 'File your taxes quickly with our simple process' 
    },
    { 
      icon: Shield,
      title: '100% Accurate', 
      description: 'Guaranteed accuracy or we pay the penalties' 
    },
    { 
      icon: Headphones,
      title: 'Support Anytime', 
      description: 'Get help when you need it from real people' 
    }
  ];

  const process = [
    { step: '1', title: 'Create Account', description: 'Sign up in under 2 minutes' },
    { step: '2', title: 'Enter Info', description: 'Answer simple questions about yourself' },
    { step: '3', title: 'Review', description: 'Check your return for accuracy' },
    { step: '4', title: 'File & Done', description: 'E-file and track your refund' }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 py-24 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Tax filing made easy for everyone.
            </h1>
            <p className="text-2xl text-blue-200 font-medium mb-4">Precision in Every Return.</p>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              File your taxes with confidence. Get your maximum refund, guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-700 hover:bg-blue-50 text-lg px-8 py-6 h-auto font-semibold" 
                onClick={() => navigate('/login')}
              >
                Start for free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto font-semibold" 
                onClick={() => navigate('/pricing')}
              >
                See Pricing
              </Button>
            </div>
            <p className="text-sm text-blue-200 mt-6">
              Free to start. Pay only when you file. Money-back guarantee.
            </p>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="container mx-auto px-4 mt-16 relative z-10">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-blue-200">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What We Offer</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to file taxes and form your business, all in one place.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-primary/50" 
                  onClick={() => navigate(service.path)}
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {service.features.map((feature, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{feature}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center text-primary font-medium group-hover:translate-x-1 transition-transform">
                      Learn More <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Why Choose Us</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Your Trusted Partner in Financial Compliance</h2>
              <p className="text-lg text-muted-foreground mb-8">
                With over 15 years of experience serving clients across 50+ countries, we bring unmatched expertise 
                in cross-border taxation and compliance.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=600" 
                alt="Professional team meeting" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">How It Works</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Streamlined Process</h2>
            <p className="text-lg text-muted-foreground">Get started in minutes with our easy 4-step process</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary to-primary/20 -translate-x-1/2" />
                )}
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Book a free consultation with our tax experts and discover how we can help you.
            </p>
            <div className="flex justify-center">
              <Button size="lg" variant="secondary" onClick={() => navigate('/contact')}>
                <Phone className="mr-2 h-5 w-5" /> Book Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}